/**
 * Campi del formato di gioco: la nuova partita (V17) li precompila dalla
 * squadra, le impostazioni della squadra (V43) li salvano come valori da
 * copiare. Un campo svuotato vale NaN, e lo schema lo rifiuta.
 */

import type { Dispatch, SetStateAction } from 'react';
import type { Formato } from '@calcio/shared';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

export function CampiFormato({
  formato,
  onCambia,
  errore,
}: {
  formato: Formato;
  onCambia: Dispatch<SetStateAction<Formato>>;
  errore?: string;
}) {
  const numero = (campo: Exclude<keyof Formato, 'rientroConsentito'>) => (valore: string) =>
    onCambia((f) => ({ ...f, [campo]: valore === '' ? Number.NaN : Number(valore) }));

  return (
    <FieldGroup>
      <div className="grid grid-cols-2 gap-4 @xl:grid-cols-4">
        <CampoNumero
          id="formato-periodi"
          etichetta="Periodi"
          valore={formato.nPeriodi}
          onCambia={numero('nPeriodi')}
        />
        <CampoNumero
          id="formato-durata"
          etichetta="Minuti per periodo"
          valore={formato.durataPeriodoMin}
          onCambia={numero('durataPeriodoMin')}
        />
        <CampoNumero
          id="formato-in-campo"
          etichetta="Giocatori in campo"
          valore={formato.giocatoriInCampo}
          onCambia={numero('giocatoriInCampo')}
        />
        <CampoNumero
          id="formato-sostituzioni"
          etichetta="Sostituzioni"
          valore={formato.maxSostituzioni}
          onCambia={numero('maxSostituzioni')}
        />
      </div>
      <Field orientation="horizontal">
        <FieldContent>
          <FieldLabel htmlFor="formato-rientro">Rientro consentito</FieldLabel>
          <FieldDescription>Chi esce può tornare in campo.</FieldDescription>
        </FieldContent>
        <Switch
          id="formato-rientro"
          checked={formato.rientroConsentito}
          onCheckedChange={(v) => onCambia((f) => ({ ...f, rientroConsentito: v }))}
        />
      </Field>
      {errore && <FieldError>{errore}</FieldError>}
    </FieldGroup>
  );
}

function CampoNumero({
  id,
  etichetta,
  valore,
  onCambia,
}: {
  id: string;
  etichetta: string;
  valore: number;
  onCambia: (valore: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{etichetta}</FieldLabel>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        value={Number.isNaN(valore) ? '' : valore}
        onChange={(e) => onCambia(e.target.value)}
      />
    </Field>
  );
}
